/**
 * Cambio de la clave temporal que entregó admisión.
 *
 * Va incrustado en la portada y no como una pantalla que bloquea: dejar entrar
 * y pedirlo con la información ya delante es lo que hace que se cambie, en vez
 * de que el paciente abandone en un muro antes de ver nada suyo.
 */
import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { cambiarPasswordPortal } from "../../api/portal";
import { usePortalAuth } from "../../auth/PortalAuthProvider";
import { formatApiError } from "../../api/client";
import { Alert, Button, Card, CardBody, Field, Input } from "../../components/ui";

export function CambiarPasswordCard() {
  const { recargar } = usePortalAuth();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  const guardar = useMutation({
    mutationFn: () => cambiarPasswordPortal(actual, nueva),
    onSuccess: async () => {
      setListo(true);
      setError(null);
      await recargar();
    },
    onError: (e) => setError(formatApiError(e, "No se pudo cambiar la contraseña.")),
  });

  if (listo) {
    return (
      <Alert variant="success">
        Listo, tu contraseña quedó cambiada. Úsala la próxima vez que entres.
      </Alert>
    );
  }

  const coinciden = nueva.length >= 8 && nueva === repetida;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!coinciden) return;
    guardar.mutate();
  }

  return (
    <Card>
      <CardBody>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <p className="flex items-center gap-2 font-semibold text-slate-900">
              <KeyRound className="h-4 w-4 text-[color:var(--color-link)]" />
              Cambia tu contraseña
            </p>
            <p className="mt-0.5 text-sm text-slate-500">
              Elige una que solo tú sepas.
            </p>
          </div>

          {error && <Alert>{error}</Alert>}

          <Field label="Contraseña actual" htmlFor="pw_act">
            <Input
              id="pw_act"
              type="password"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tu nueva contraseña" htmlFor="pw_new" hint="Mínimo 8 caracteres.">
              <Input
                id="pw_new"
                type="password"
                value={nueva}
                onChange={(e) => setNueva(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field
              label="Repítela"
              htmlFor="pw_rep"
              error={
                repetida.length > 0 && nueva !== repetida
                  ? "No coinciden"
                  : undefined
              }
            >
              <Input
                id="pw_rep"
                type="password"
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={!actual || !coinciden} loading={guardar.isPending}>
              Guardar contraseña
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
